/**
 * 68.22 step 2 (DIRECTION §0.9; DECISIONS 159, the owner: "1. auto, 2. sure, 3. sure, 4. sure 5. sure"): the play — one
 * order over several squads, each its role, toward one objective.
 *
 * - **One input** (`coordinate`: the team, two squads or more, the objective) is expanded here into a task per squad that
 *   names the play (`SquadTask.play`), so possession, re-entry and replays rebuild it from the state like any order.
 * - **Roles, automatic (159 (1)):** each squad's own support position (core/support.ts) is worked out; the squad nearest
 *   its position supports, the rest assault. Two roles (159 (4)).
 * - **Phases, read from the state once a second (`stepPlays`; match/rates.ts `RATES.plays`):**
 *   1. *setting* — the support holds its position (a placed `hold` task there), the assault waits at its jump-off (a
 *      `move` to the band's far edge on its own side);
 *   2. *assault* — once a support member stands at the position (or PLAY.setSeconds pass: 159 (2), the assault waits for
 *      the support), the assault bounds to the objective (today's `bound`);
 *   3. *lift and shift* (159 (3), on): when the assault is within PLAY.liftPeople of the objective the play records it
 *      (`lifted`); the support's brain reads it to stop firing at the objective (lane C's role brain; today's support
 *      holds its position);
 *   4. *done* — the objective taken (the team holds the point, or the assault stands on the place), or the assault fallen
 *      or gone: the play's tasks are cleared from its squads, so nothing of it lingers.
 *   A squad given another order leaves the play (its in-force order no longer the play's).
 *
 * Today's verbs carry the roles until lane C's role brains (hold a place and fire at an area; wait, then bound) land.
 * Pure; behind no switch — nothing makes a play but the input.
 */
import type { GameState, MatchInputBody, Play, PlayRefusal, SquadOrder, StructureState, Vec3 } from '../types';
import { supportSites, supportPositionAmong, rifleEffectivePeople, SUPPORT_POSITION } from './support';
import { teamPicture } from './picture';
import { applyOrderInput, squadOf, squadTaskFor } from './squads';
import { orderPlace } from './orderForms';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecDot, vecNormalize, vecScale, vecSub, vecLength } from '../math';
import { newConvoy, reformConvoy, stepConvoy } from './convoyMarch';
import { dacos, dcos, dsin } from './dmath';
import { PHYS_DT } from '../constants';
import { brainFace } from './brainFace';
import { flankWaypoint, FLANK } from './flankRoute';
import { localForce } from './localForce';
import { structureTeam } from './body';
import { seatTaken } from './seats';
import { callMortar } from './mortar';
import { callDropship, dropshipsLeft, gunshipsLeft, shipBase } from './dropship';
import { hotWanted } from './hotCall';
import { SHIP_RIDE } from './shipRide';
import { countOf } from './defaultOn';

/** The dials: how long the assault waits for its support at most, how near (people) the assault lifts the support's fire,
 *  how near (people) a body stands "at" a place, and how fresh (s) a team's track of an enemy counts toward `playContested`. */
export const PLAY = { setSeconds: 45, liftPeople: 6, atPeople: 2, knownSeconds: 10, quietSeconds: 60 };
/**
 * 69.37 step 1 (ARCHITECTURE §5.28): the mounted assault's dials — an assault squad is at its jump-off on foot within
 * `arrivePeople` of it; the play waits `setSeconds` at most for its rides and its support (the march is longer than a walk) —
 * with a convoy (69.38) counted from its halt, and `marchSeconds` at most from the play's issue (lane E's 69.61 step 5 finding:
 * on the ½ board the 180 s from the issue ran out with the column 1,000–2,200 people short, the assault going on foot and the
 * convoy never halting); step 4, its air element lands `airBehindPeople` behind the objective (beyond it from the side's ship
 * base); step 5, a bot commander orders one at an objective at least `walkPeople` from the nearest of its squads (beyond a
 * walk). Proposals.
 */
export const MOUNTED = { arrivePeople: 20, setSeconds: 180, marchSeconds: 600, airBehindPeople: 40, walkPeople: 300 };

type CoordinateBody = Extract<MatchInputBody, { kind: 'coordinate' }>;

const person = (state: Pick<GameState, 'config'>) => PERSON_BODY_HEIGHT * personSetOf(state).k;
const arcPeople = (state: GameState, planetIndex: number, a: Vec3, b: Vec3) =>
  (dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * state.planets[planetIndex].radius) / person(state);
const living = (state: GameState, squadId: number) => {
  const sq = state.squads?.find(s => s.id === squadId);
  return sq ? sq.unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is NonNullable<typeof p> => !!p && p.alive) : [];
};
function centreOf(state: GameState, squadId: number): Vec3 | null {
  const us = living(state, squadId);
  if (!us.length) return null;
  const sum = us.reduce((a, u) => vecAdd(a, vecNormalize(u.surfaceDir)), { x: 0, y: 0, z: 0 });
  return vecLength(sum) > 1e-9 ? vecNormalize(sum) : null;
}
/** The place `people` from `objective` toward `toward`, along the ground. */
function toward(state: GameState, planetIndex: number, objective: Vec3, to: Vec3, people: number): Vec3 {
  const o = vecNormalize(objective), t0 = vecSub(vecNormalize(to), vecScale(o, vecDot(vecNormalize(to), o)));
  const way = vecLength(t0) > 1e-9 ? vecNormalize(t0) : vecNormalize(vecSub({ x: 1, y: 0, z: 0 }, vecScale(o, o.x)));
  const a = (people * person(state)) / state.planets[planetIndex].radius;
  return vecNormalize(vecAdd(vecScale(o, dcos(a)), vecScale(way, dsin(a))));
}

/** Apply a task to one squad through the order path (its forms' rules), tagged with the play. */
function task(state: GameState, team: number, squadId: number, order: SquadOrder, tick: number, play: number, role: 'support' | 'assault' | undefined, by: CoordinateBody['by']): GameState | string {
  const r = taskOrRefusal(state, team, squadId, order, tick, play, role, by);
  return 'refused' in r ? r.refused : r;
}
function taskOrRefusal(state: GameState, team: number, squadId: number, order: SquadOrder, tick: number, play: number, role: 'support' | 'assault' | undefined, by: CoordinateBody['by']): GameState | { refused: string; code: string } {
  // (69.72 step 5, DECISIONS 304: the play goes with the order, so it applies at a play's rank — 4)
  const r = applyOrderInput(state, { kind: 'order', team, to: { squad: squadId }, tier: 'task', order, play, ...(role ? { role } : {}), ...(by ? { by } : {}) }, tick);
  return 'refused' in r ? r : r.state;
}

/**
 * 68.22 (the owner on master: "Role brain / team planning is doing a decent job getting troops to the objective, but they're
 * waiting around for other troops to show up … if holding guard ~20 humans away from the objective with no enemies in
 * sight, might as well hold guard on the objective"): does the play's team know of an enemy within the support band of the
 * objective — its picture (core/picture.ts teamPicture, the fairness rule's one accessor: its shared tracks, what its
 * relays, towers and drones see, its marks), a belief from within PLAY.knownSeconds, or what its people see at their last
 * look (their brains, in the state: the picture carries those only when the team shares its tracks)? Only what the team
 * knows: an enemy unseen there does not contest it.
 */
export function playContested(state: GameState, p: Play, place: { planetIndex: number; surfaceDir: Vec3 }, tick: number): boolean {
  const band = SUPPORT_POSITION.share * rifleEffectivePeople() * (1 + SUPPORT_POSITION.tolerance);
  const enemy = (id: number) => { const e = state.players.find(q => q.id === id); return !!e && e.alive && e.teamIndex !== p.team; };
  const within = (d: Vec3) => arcPeople(state, place.planetIndex, d, place.surfaceDir) <= band;
  for (const b of teamPicture(state, p.team, tick)) {
    if (b.planetIndex === place.planetIndex && (tick - b.tick) * PHYS_DT <= PLAY.knownSeconds && (b.unitId === undefined || enemy(b.unitId)) && within(b.surfaceDir)) return true;
  }
  for (const u of state.players) {
    if (!u.alive || u.teamIndex !== p.team) continue;
    for (const x of brainFace(state, u.id)?.seenAt ?? []) if (x.planetIndex === place.planetIndex && enemy(x.id) && within(x.surfaceDir)) return true;
  }
  return false;
}

/** A `coordinate` input: the play made — its roles, its support position, the first phase's tasks. */
export function applyCoordinate(state: GameState, body: CoordinateBody, tick: number): { state: GameState; play: Play } | { refused: string; code: string } {
  const ids = [...new Set(body.squads)].sort((a, b) => a - b);
  if (ids.length < 2) return { refused: 'A play needs two squads or more', code: 'squads' };
  for (const id of ids) {
    const sq = state.squads?.find(s => s.id === id);
    if (!sq) return { refused: `No squad ${id}`, code: 'squad' };
    if (sq.team !== body.team) return { refused: `Squad ${id} is not team ${body.team}'s`, code: 'team' };
  }
  const place = 'pointId' in body.objective && body.objective.pointId !== undefined
    ? orderPlace(state, { kind: 'hold', pointId: body.objective.pointId })
    : orderPlace(state, { kind: 'hold', planetIndex: (body.objective as { planetIndex: number }).planetIndex, surfaceDir: (body.objective as { surfaceDir: Vec3 }).surfaceDir });
  if (!place) return { refused: 'No such objective', code: 'place' };
  const live = ids.map(id => ({ id, at: centreOf(state, id) })).filter((x): x is { id: number; at: Vec3 } => x.at !== null);
  if (live.length < 2) return { refused: 'A play needs two squads with someone standing', code: 'squads' };
  // 159 (1): the squad nearest its own support position supports — or (66.15) the squads the input names, firing from where
  // they stand (`here`: the standoff's base of fire is left as it is)
  const named = body.support?.length ? live.filter(x => body.support!.includes(x.id)) : [];
  if (body.support?.length && (named.length === 0 || named.length === live.length)) return { refused: 'A play needs a support and an assault', code: 'squads' };
  // (the objective's sites read once for the play, every squad's position the nearest of them to it: the same as each squad's
  // own supportPosition — a search per squad was 97 % of a play's ~15 ms at 25 a side, 6–8 squads on mega25)
  const sites = body.here ? null : supportSites(state, place.planetIndex, place.surfaceDir)!;
  const options = live.map(x => ({ ...x, pos: sites ? supportPositionAmong(sites, x.at) : { dir: x.at, kind: 'open' as const } }));
  const support = named.length ? options.find(x => x.id === named[0].id)! : options.reduce((best, x) => arcPeople(state, place.planetIndex, x.at, x.pos.dir) < arcPeople(state, place.planetIndex, best.at, best.pos.dir) ? x : best);
  const supporters = named.length ? named.map(x => x.id) : [support.id];
  const assault = live.filter(x => !supporters.includes(x.id));
  const id = (state.plays ?? []).reduce((m, p) => Math.max(m, p.id + 1), 0);
  // 69.37 step 1 (§5.28): a mounted assault — each assault squad's APC, the fire-support vehicles (the switch off: on foot)
  const forces = body.mounted && state.config.mountedAssault ? mountedForces(state, body.team, assault) : null;
  const air = forces ? airElement(state, body.team, place, assault, forces.rides) : null;   // (step 4)
  const far = SUPPORT_POSITION.share * rifleEffectivePeople() * (1 + SUPPORT_POSITION.tolerance);
  const jumpOff: Record<number, Vec3> = Object.fromEntries(assault.map(x => [x.id, x.id === air?.squadId ? air.land : body.here ? x.at : toward(state, place.planetIndex, place.surfaceDir, x.at, far)]));
  // 69.38 (the crews; §5.28's "Assemble: … the vehicles are crewed"): the play's support squads crew its fire support with
  // nobody in the driver's seat, the tank first — the support is the play's base of fire, and at 12 a side every other squad
  // had its task when the story's opening plays were called (the ride, the strike, the builds): the tank was crewed on none
  // of five sides, and the column waited for it, then left it. A vehicle with no driver and no crew told left out of the
  // column (a crew that comes later takes it to its post on its own)
  const crews: Record<number, number> = {};
  if (forces) {
    const empty = forces.fire.map(fid => state.structures.find(v => v.id === fid)).filter((v): v is StructureState => !!v && !v.disabled && !(v.seats ?? []).some(z => z.role === 'driver' && seatTaken(z)))
      .sort((a, b) => Number(b.type === 'tank') - Number(a.type === 'tank') || a.id - b.id);
    supporters.forEach((sid, i) => { if (empty[i]) crews[sid] = empty[i].id; });
  }
  const crewed = (fid: number) => Object.values(crews).includes(fid) || !!state.structures.find(v => v.id === fid)?.seats?.some(z => z.role === 'driver' && seatTaken(z));
  // 69.38 (§5.28.1, DECISIONS 274): its vehicles march as a convoy, halting at the first ride's jump-off (no ride: the support's position)
  const first = forces ? Object.keys(forces.rides).map(Number).sort((a, b) => a - b)[0] : undefined;
  const convoy = forces ? newConvoy(state, forces.rides, forces.fire.filter(crewed), first !== undefined ? jumpOff[first] : support.pos.dir, tick) : null;
  const mounted = forces ? { ...forces, ...(air ? { air } : {}), ...(convoy ? { convoy } : {}), ...(Object.keys(crews).length ? { crews } : {}) } : null;
  const play: Play = {
    id, team: body.team, objective: body.objective, support: supporters, assault: assault.map(x => x.id),
    position: { planetIndex: place.planetIndex, surfaceDir: support.pos.dir, kind: support.pos.kind },
    // (69.37 step 4: the air element's squad jumps off where its ship lands it — its walk there the wave ship's ride, core/shipRide.ts)
    jumpOff,
    phase: 'setting', issuedTick: tick, ...(body.by ? { by: body.by } : {}), ...(body.here ? { here: true as const } : {}), ...(mounted ? { mounted } : {}),
    // 66.16: each assault squad's dead-ground waypoint off its line (away from the support), where the ground has one
    ...(body.flank ? (() => {
      const via: NonNullable<Play['via']> = {};
      // (the corridors already taken: the team's assaults under way, then this play's own as they are chosen — Killzone's spread)
      const taken: Vec3[] = (state.plays ?? []).filter(q => q.team === body.team && q.phase !== 'done').flatMap(q => Object.values(q.via ?? {}).map(v => v.at));
      for (const x of assault) { const at = flankWaypoint(state, place.planetIndex, x.at, place.surfaceDir, body.team, tick, taken); if (at) { via[x.id] = { at, state: 'route', since: tick }; taken.push(at); } }
      return Object.keys(via).length ? { via } : {};
    })() : {}),
  };
  let s: GameState | string = state;
  for (const sid of supporters) {
    if (typeof s === 'string') break;
    const at = options.find(x => x.id === sid)!.pos.dir;
    // (69.38, the crews: a support squad told to crew the play's fire support crews it — its driver to the column, then the post)
    const v = crews[sid] !== undefined ? state.structures.find(x => x.id === crews[sid]) : undefined;
    const driverSeat = v ? (v.seats ?? []).findIndex(z => z.role === 'driver') : -1;
    s = task(s, body.team, sid, v && driverSeat >= 0 ? { kind: 'crew', structureId: v.id, seat: driverSeat } : { kind: 'hold', planetIndex: place.planetIndex, surfaceDir: at }, tick, id, 'support', body.by);
  }
  for (const x of assault) {
    if (typeof s === 'string') break;
    // (69.37: a squad with an APC rides it to its jump-off, its gunner kept on the .50 at the dismount; else it walks there)
    const apc = mounted?.rides[x.id];
    s = task(s, body.team, x.id, apc !== undefined ? { kind: 'ride', structureId: apc, keep: 'gunner', planetIndex: place.planetIndex, surfaceDir: play.jumpOff[x.id] }
      : { kind: 'move', planetIndex: place.planetIndex, surfaceDir: play.jumpOff[x.id] }, tick, id, 'assault', body.by);
  }
  if (typeof s === 'string') return { refused: s, code: 'order' };
  return { state: { ...s, plays: [...(s.plays ?? []), play] }, play };
}

/**
 * 69.37 step 1 (ARCHITECTURE §5.28): a mounted assault's forces — for each assault squad (in id order) the nearest standing APC
 * of its side (the 6×6, the tracked carrier; not a mortar carrier) no live play rides already, and the side's standing tanks and
 * technicals as the fire support (they cover the play's objective: ai/technicalLoop.ts technicalObjective).
 */
function mountedForces(state: GameState, team: number, assault: ReadonlyArray<{ id: number; at: Vec3 }>): NonNullable<Play['mounted']> {
  const apcs = freeApcs(state, team);
  const rides: Record<number, number> = {};
  for (const x of [...assault].sort((a, b) => a.id - b.id)) {
    let best: StructureState | null = null, bestD = Infinity;
    for (const v of apcs) {
      if (Object.values(rides).includes(v.id)) continue;
      const d = arcPeople(state, v.planetIndex, v.surfaceDir, x.at);
      if (d < bestD) { bestD = d; best = v; }
    }
    if (best) rides[x.id] = best.id;
  }
  return { rides, fire: fireSupport(state, team).map(v => v.id) };
}

const standingOf = (state: GameState, team: number) => (v: StructureState) => v.health > 0 && structureTeam(state, v) === team;
/** The side's standing APCs (the 6×6, the tracked carrier; not a mortar carrier) no live play rides. */
function freeApcs(state: GameState, team: number): StructureState[] {
  const ridden = new Set((state.plays ?? []).filter(q => q.phase !== 'done' && q.mounted).flatMap(q => Object.values(q.mounted!.rides)));
  const mine = standingOf(state, team);
  return state.structures.filter(v => (v.type === 'wheeled' || v.type === 'tracked') && v.armament !== 'mortar' && mine(v) && !ridden.has(v.id));
}
/** The side's standing fire-support vehicles: its tanks and technicals (not a mortar), in id order. */
function fireSupport(state: GameState, team: number): StructureState[] {
  const mine = standingOf(state, team);
  return state.structures.filter(v => (v.type === 'tank' || (v.type === 'technical' && v.armament !== 'mortar')) && mine(v)).sort((a, b) => a.id - b.id);
}

/**
 * 69.37 step 5 (ARCHITECTURE §5.28; DECISIONS 268 (2) (a), (b), as leaned): does a bot commander order its play at `place`
 * mounted — a combined force ready (an APC of its side no live play rides, which the assault crews by its ride, and one
 * fire-support vehicle at least) and the objective beyond a walk (MOUNTED.walkPeople from the nearest of `from`, its squads'
 * people)?
 */
export function mountedReady(state: GameState, team: number, from: readonly Vec3[], place: { planetIndex: number; surfaceDir: Vec3 }): boolean {
  if (!from.length || !mountedForce(state, team)) return false;
  return Math.min(...from.map(d => arcPeople(state, place.planetIndex, d, place.surfaceDir))) >= MOUNTED.walkPeople;
}

/**
 * The combined force alone (an APC of the side no live play rides, and a fire-support vehicle), with no walk: the story's
 * escort (69.61 step 5) is a column whatever the distance — on story-6…8 its point lay 202–283 people from home, inside the
 * walk, and no side ever formed it.
 */
export function mountedForce(state: GameState, team: number): boolean {
  return freeApcs(state, team).length > 0 && fireSupport(state, team).length > 0;
}

/**
 * 69.37 step 4 (ARCHITECTURE §5.28; DECISIONS 268 (2) (c), "c": the plan's air element first): a mounted play's air element —
 * the side's ship landing MOUNTED.airBehindPeople behind the objective (beyond it from its ship base) as the assault goes. With
 * the side's wave ship on its pad on the objective's planet, it carries the first assault squad (in id order) with no ride that
 * stands at the base (within SHIP_RIDE.basePeople of the pad): it boards, and the ship is held till the assault. Else, with a
 * called dropship left, the play calls it at the assault for its side's waiting. Neither: none. The call names no kind of ship
 * (the side's own, whatever it flies).
 */
function airElement(state: GameState, team: number, place: { planetIndex: number; surfaceDir: Vec3 }, assault: ReadonlyArray<{ id: number; at: Vec3 }>, rides: Record<number, number>): NonNullable<NonNullable<Play['mounted']>['air']> | null {
  const base = shipBase(state, team);
  if (!base || base.planetIndex !== place.planetIndex) return null;
  const land = toward(state, place.planetIndex, place.surfaceDir, base.surfaceDir, -MOUNTED.airBehindPeople);
  const pad = state.shipPads?.[team];
  if (pad && pad.planetIndex === place.planetIndex) {
    const at = [...assault].sort((a, b) => a.id - b.id).find(x => rides[x.id] === undefined && arcPeople(state, place.planetIndex, x.at, pad.dir) <= SHIP_RIDE.basePeople);
    if (at) return { land, squadId: at.id };
  }
  return dropshipsLeft(state, team) > 0 ? { land } : null;
}

/** 69.37 step 4: does one of the side's live plays want its called dropship — an air element not flown, with no wave ship's squad? */
export function airWanted(state: GameState, team: number): boolean {
  return (state.plays ?? []).some(p => p.team === team && p.phase !== 'done' && p.mounted?.air && p.mounted.air.squadId === undefined && p.mounted.air.flown === undefined);
}

/** 69.37 step 4: the side's live play whose air element its wave ship flies (the lowest id), with its landing; or null. */
export function airDest(state: GameState, team: number): { play: number; planetIndex: number; land: Vec3 } | null {
  const p = (state.plays ?? []).filter(q => q.team === team && q.phase !== 'done' && q.mounted?.air?.squadId !== undefined && q.mounted.air.flown === undefined).sort((a, b) => a.id - b.id)[0];
  return p ? { play: p.id, planetIndex: p.position.planetIndex, land: p.mounted!.air!.land } : null;
}

/** 69.37 step 4: play `id`'s air element took off at `tick` (its wave ship's sortie launched). */
export function airFlown(state: GameState, id: number, tick: number): GameState {
  return { ...state, plays: (state.plays ?? []).map(p => (p.id === id && p.mounted?.air && p.mounted.air.flown === undefined ? { ...p, mounted: { ...p.mounted, air: { ...p.mounted.air, flown: tick } } } : p)) };
}

/** 69.37 step 4: a play in its assault calls its air element's dropship (no wave ship's squad, not flown yet) — landed behind the objective, its riders to take it; refused (nobody waiting yet): again next second. */
function callAir(state: GameState, p: Play, place: { planetIndex: number; surfaceDir: Vec3 }, tick: number): { state: GameState; play: Play } {
  const air = p.mounted?.air;
  if (!air || air.squadId !== undefined || air.flown !== undefined) return { state, play: p };
  const at = { planetIndex: place.planetIndex, surfaceDir: air.land };
  const order: SquadOrder = 'pointId' in p.objective && p.objective.pointId !== undefined ? { kind: 'take', pointId: p.objective.pointId } : { kind: 'move', planetIndex: place.planetIndex, surfaceDir: place.surfaceDir };
  const r = callDropship(state, p.team, at, tick, { by: p.by ?? 'commander', order, ...(hotWanted(state, p.team, at) ? { hot: true } : {}) });
  if ('refused' in r) return { state, play: p };
  return { state: r.state, play: { ...p, mounted: { ...p.mounted!, air: { ...air, flown: tick } } } };
}

/**
 * 69.37: a mounted play's assault arrived — each squad on foot (its ride's gunner, kept at its gun by the ride's `keep`, aside:
 * the escort's trace had the play wait out its whole limit for him), one of it within MOUNTED.arrivePeople of its jump-off (a
 * play on foot: always); its air element's squad (step 4) comes by its ship, at the assault.
 */
export function mountedArrived(state: GameState, p: Play): boolean {
  if (!p.mounted) return true;
  const gunner = (u: { mount?: { bodyId: number; seat: number } }, id: number): boolean => !!u.mount && u.mount.bodyId === p.mounted!.rides[id] && state.structures.find(x => x.id === u.mount!.bodyId)?.seats?.[u.mount.seat]?.role === 'weapon';
  return p.assault.filter(id => id !== p.mounted!.air?.squadId).every(id => { const ms = living(state, id); return ms.every(u => !u.mount || gunner(u, id)) && ms.some(u => !u.mount && arcPeople(state, p.position.planetIndex, u.surfaceDir, p.jumpOff[id]) <= MOUNTED.arrivePeople); });
}

/** 69.37's fix: squad `squadId` without play `play`'s ride tasks (a re-task replaces its ride, never stacks one on it: a stale ride surfacing after the new one ended put the riders back in and out of the APC). */
function withoutRides(state: GameState, squadId: number, play: number): GameState {
  return { ...state, squads: (state.squads ?? []).map(q => (q.id !== squadId || !q.tasks.some(t => t.play === play && t.order.kind === 'ride') ? q : { ...q, tasks: q.tasks.filter(t => !(t.play === play && t.order.kind === 'ride')) })) };
}

/** Is a squad's order in force still the play's? */
function inPlay(state: GameState, squadId: number, play: number, tick: number): boolean {
  const lead = living(state, squadId)[0];
  return !!lead && squadTaskFor(state, lead.id, tick)?.play === play;
}

/** The play's phases, once a second: the assault goes, the support lifts, the play ends. */
export function stepPlays(state: GameState, tick: number): GameState {
  return stepPlaysLogged(state, tick).state;
}

/**
 * stepPlays, with the play's own re-tasks the Match refused (69.72 step 5, DECISIONS 304: a refusal is shown to its giver).
 * Each is dropped as before (the squad keeps what it had); the list is the Match's to keep beside the state.
 */
export function stepPlaysLogged(state: GameState, tick: number): { state: GameState; refusals: PlayRefusal[] } {
  const refusals: PlayRefusal[] = [];
  if (!state.plays?.some(p => p.phase !== 'done')) return { state, refusals };
  let s = state;
  const plays: Play[] = [];
  /** A play's re-task of squad `id`: the state with it, or the state as it was and the refusal noted. */
  const retask = (at: GameState, p: Play, id: number, order: SquadOrder, role: 'support' | 'assault' | undefined, step: PlayRefusal['step']): GameState => {
    const r = taskOrRefusal(at, p.team, id, order, tick, p.id, role, p.by);
    if (!('refused' in r)) return r;
    refusals.push({ tick, play: p.id, squadId: id, step, code: r.code, reason: r.refused });
    return s;
  };
  for (const p0 of state.plays) {
    if (p0.phase === 'done') { plays.push(p0); continue; }
    const p: Play = { ...p0, support: p0.support.filter(id => inPlay(s, id, p0.id, tick)), assault: p0.assault.filter(id => inPlay(s, id, p0.id, tick)) };
    const place = 'pointId' in p.objective && p.objective.pointId !== undefined
      ? orderPlace(s, { kind: 'hold', pointId: p.objective.pointId })
      : orderPlace(s, { kind: 'hold', planetIndex: (p.objective as { planetIndex: number }).planetIndex, surfaceDir: (p.objective as { surfaceDir: Vec3 }).surfaceDir });
    const near = (ids: number[], at: Vec3, people: number) => ids.some(id => living(s, id).some(u => arcPeople(s, p.position.planetIndex, u.surfaceDir, at) <= people));
    const site = 'pointId' in p.objective && p.objective.pointId !== undefined ? s.modeState.points?.sites.find(x => x.id === (p.objective as { pointId: number }).pointId) : undefined;
    // (the squads that went on to hold the objective while it was quiet: their task the play's, with no role)
    const wentOn = [...p.support, ...p.assault].filter(id => { const lead = living(s, id)[0]; const t = lead ? squadTaskFor(s, lead.id, tick) : null; return !!t && t.play === p.id && !t.role; });
    const taken = site ? site.owner === p.team && site.capturer === null
      : !!place && ((p.phase === 'assault' && near(p.assault, place.surfaceDir, PLAY.atPeople)) || (p.phase === 'setting' && near(wentOn, place.surfaceDir, PLAY.atPeople)));
    // (69.61 step 5: the story's escort column — a mounted play at its side's open escort point — is not over for its point
    // being held already: it goes on to its halt and its rides in, which is what the escort counts (core/storyObjectives.ts
    // stepEscort). Ended at once, a column re-formed at a point its side had taken never marched: lab-7 on large, team 0, a
    // play formed and over every 10 s from 310 s. Stopped short in its herringbone it is no column — the escort counts it
    // stopped — and ends as any play)
    const column = !!p.mounted?.convoy && p.mounted.convoy.halted?.how !== 'herringbone' && site !== undefined && (s.modeState.objectives ?? []).some(o => o.kind === 'escort' && o.team === p.team && o.doneTick === undefined && o.pointId === site.id) && !mountedArrived(s, p);
    if (!place || (taken && !column) || p.assault.length === 0) {
      // done: the play's tasks leave its squads (nothing of it lingers) — but taken quietly, those holding the objective
      // keep holding it, a plain hold now (the play dissolves into holds)
      const quietly = taken && p.phase === 'setting';
      s = { ...s, squads: (s.squads ?? []).map(q => !q.tasks.some(t => t.play === p.id) ? q : { ...q, tasks: q.tasks.flatMap(t => {
        if (t.play !== p.id) return [t];
        if (quietly && !t.role) { const { play: _p, ...plain } = t; return [plain]; }
        return [];
      }) }) };
      plays.push({ ...p, phase: 'done' });
      continue;
    }
    // 69.38 (§5.28.1, DECISIONS 274): a mounted play's convoy on the march — its look; at its halt (a wedge at the halt point,
    // a herringbone on contact) each riding squad's jump-off moves to its APC's slot, its ride re-tasked there
    const rideTo = (jumpOff: Record<number, Vec3>) => {
      for (const [k, at] of Object.entries(jumpOff)) {
        const sid = Number(k), apc = p.mounted!.rides[sid];
        if (!p.assault.includes(sid) || apc === undefined) continue;
        p.jumpOff = { ...p.jumpOff, [sid]: at };
        s = retask(withoutRides(s, sid, p.id), p, sid, { kind: 'ride', structureId: apc, keep: 'gunner', planetIndex: place.planetIndex, surfaceDir: at }, 'assault', 'ride');
      }
    };
    // (274 (c), B: halted on contact, the column re-forms once the contact is gone — each riding squad's ride back to its
    // jump-off before the drill, and the march on from where it stands, this second)
    if (p.phase === 'setting' && p.mounted?.convoy?.halted?.how === 'herringbone') {
      const r = reformConvoy(s, p, tick);
      p.mounted = { ...p.mounted, convoy: r.convoy };
      if (r.jumpOff) rideTo(r.jumpOff);
    }
    if (p.phase === 'setting' && p.mounted?.convoy && !p.mounted.convoy.halted) {
      const r = stepConvoy(s, p, place.surfaceDir, tick);
      p.mounted = { ...p.mounted, convoy: r.convoy };
      rideTo(r.slots ?? {});
    }
    // 68.22 (the owner, above): no enemy known within the support band of the objective — a squad that has reached its place
    // goes on and holds the objective itself (with cover at the point: it takes it, and it is the base of fire if contact
    // comes). The wait (for the support, PLAY.setSeconds at most) is for a contested objective only
    // (the contest, stored each second for the table and the card: "quiet" or "contested")
    const contested = playContested(s, p, place, tick);
    if (contested) p.contested = true; else delete p.contested;
    // 69.6c (lane C's Foothold diagnostic: the two longest stalls were a play parked in its quiet setting, its holders posted
    // round the objective 13 people off and nobody stepping on, 500 s and 370 s; plays a median 100 s in setting): past
    // PLAY.quietSeconds quiet, the play dissolves — its tasks leave the squads, which go back to their own orders (a take
    // steps on the point)
    // (69.37: a mounted play's quiet rules wait for its rides; 274 (c), B: never in a herringbone — dismounted short of the
    // objective, its squads fight the contact and re-form on the column, not go on afoot or dissolve: lab-7's escorts at 60 s)
    const arrived = mountedArrived(s, p) && p.mounted?.convoy?.halted?.how !== 'herringbone';
    if (p.phase === 'setting' && !contested && arrived && (tick - p.issuedTick) * PHYS_DT >= PLAY.quietSeconds) {
      s = { ...s, squads: (s.squads ?? []).map(q => !q.tasks.some(t => t.play === p.id) ? q : { ...q, tasks: q.tasks.filter(t => t.play !== p.id) }) };
      plays.push({ ...p, phase: 'done', dissolvedQuiet: tick });
      continue;
    }
    if (p.phase === 'setting' && !contested && arrived) {
      const objectiveHold: SquadOrder = site ? { kind: 'hold', pointId: site.id } : { kind: 'hold', planetIndex: place.planetIndex, surfaceDir: place.surfaceDir };
      for (const id of [...p.support, ...p.assault]) {
        if (wentOn.includes(id)) continue;
        const own = p.support.includes(id) ? p.position.surfaceDir : p.jumpOff[id];
        if (!own || !near([id], own, PLAY.atPeople)) continue;
        s = retask(s, p, id, objectiveHold, undefined, 'hold');
      }
      plays.push(p);
      continue;
    }
    // (66.15: a support firing from where it stands — `here` — is set at once)
    // (69.37: a mounted play's assault goes once its rides are in — dismounted at their jump-offs — and its support set, or
    // MOUNTED.setSeconds; with a convoy, the wait from its halt — none while it marches — and MOUNTED.marchSeconds from the issue)
    const setBy = p.mounted ? MOUNTED.setSeconds : PLAY.setSeconds, convoy = p.mounted?.convoy;
    const waited = convoy ? (convoy.halted ? (tick - convoy.halted.tick) * PHYS_DT >= setBy : false) || (tick - p.issuedTick) * PHYS_DT >= MOUNTED.marchSeconds : (tick - p.issuedTick) * PHYS_DT >= setBy;
    if (p.phase === 'setting' && !contested && !arrived && !waited) { plays.push(p); continue; }
    if (p.phase === 'setting' && (((p.here || near(p.support, p.position.surfaceDir, PLAY.atPeople)) && arrived) || waited)) {
      // 69.37 step 1: the mortar's preparation — the side's set mortar called on the objective as the assault goes (once; none set: none)
      let prepped: number | undefined;
      if (p.mounted && p.mounted.prepped === undefined) {
        const r = callMortar(s, p.team, { planetIndex: place.planetIndex, surfaceDir: place.surfaceDir }, tick, { spread: true });   // (69.67: laid a step a tick)
        if (!('refused' in r)) { s = r.state; prepped = tick; }
      }
      // 159 (2): the support is set (or the wait is over) — the assault bounds to the objective; 66.16: a squad with a flank
      // route goes to its waypoint first (the support's fire on the objective all the while)
      const via = p.via ? Object.fromEntries(Object.entries(p.via).map(([k, v]) => [k, { ...v, state: 'route' as const, since: tick }])) : undefined;
      for (const id of p.assault) {
        const w = via?.[id];
        if (p.mounted) s = withoutRides(s, id, p.id);   // (69.37's fix: dismounted, its rides done — none to surface after its bound)
        s = retask(s, p, id, w ? { kind: 'move', planetIndex: place.planetIndex, surfaceDir: w.at } : { kind: 'bound', planetIndex: place.planetIndex, surfaceDir: place.surfaceDir }, 'assault', 'go');
      }
      const went: Play = { ...p, phase: 'assault', assaultAt: tick, ...(via ? { via } : {}), ...(p.mounted && prepped !== undefined ? { mounted: { ...p.mounted, prepped } } : {}) };
      const a = callAir(s, went, place, tick);   // (69.37 step 4: and its air element's dropship)
      s = a.state;
      // 63.13: and the side's gunship over the objective, when it holds one and none of its own is up (it fires clear of the assault's people)
      if (countOf(s.config, 'gunships') && gunshipsLeft(s, p.team) > 0 && !s.dropships?.flights.some(f => f.team === p.team && f.gunship)) {
        const g = callDropship(s, p.team, { planetIndex: place.planetIndex, surfaceDir: place.surfaceDir }, tick, { payload: 'gunship', by: p.by ?? 'commander' });
        if (!('refused' in g)) s = g.state;
      }
      plays.push(a.play);
      continue;
    }
    // (69.37 step 4: an air element's dropship refused at the assault — nobody waiting yet — called again each second)
    if (p.phase === 'assault' && p.mounted?.air && p.mounted.air.squadId === undefined && p.mounted.air.flown === undefined) {
      const a = callAir(s, p, place, tick);
      s = a.state;
      Object.assign(p, a.play);
    }
    // 66.16: the flank routes — at the waypoint, wait for the local read's advantage round the objective (a fresh contact,
    // the assault's living and ours there over their believed number) or the wait's end, then go for the objective from there
    if (p.phase === 'assault' && p.via && Object.values(p.via).some(v => v.state !== 'go')) {
      const via = { ...p.via };
      for (const [k, v0] of Object.entries(p.via)) {
        const id = Number(k);
        if (v0.state === 'go' || !p.assault.includes(id)) continue;
        let v = v0;
        const age = (tick - v.since) * PHYS_DT;
        if (v.state === 'route' && (near([id], v.at, FLANK.arrivePeople) || age >= FLANK.routeSeconds)) {
          v = { ...v, state: 'wait', since: tick };
          s = retask(s, p, id, { kind: 'hold', planetIndex: place.planetIndex, surfaceDir: v.at }, 'assault', 'flankWait');
        }
        if (v.state === 'wait') {
          const lf = localForce(s, p.team, place, FLANK.readPeople, tick);
          const live = lf.enemies >= 0.5 && lf.freshest !== null && lf.freshest <= PLAY.knownSeconds;
          const ratio = (living(s, id).length + lf.friends) / Math.max(lf.enemies, 0.5);
          if ((live && ratio >= FLANK.commitRatio) || lf.freshest === null || (tick - v.since) * PHYS_DT >= FLANK.waitSeconds) {
            v = { ...v, state: 'go', since: tick, goAt: tick };
            s = retask(s, p, id, { kind: 'bound', planetIndex: place.planetIndex, surfaceDir: place.surfaceDir }, 'assault', 'flankGo');
          }
        }
        via[id] = v;
      }
      const lifted = !p.lifted && near(p.assault, place.surfaceDir, PLAY.liftPeople);
      plays.push({ ...p, via, ...(lifted ? { lifted: true as const } : {}) });
      continue;
    }
    if (p.phase === 'assault' && !p.lifted && near(p.assault, place.surfaceDir, PLAY.liftPeople)) { plays.push({ ...p, lifted: true }); continue; }   // 159 (3)
    plays.push(p);
  }
  return { state: { ...s, plays }, refusals };
}
